/* eslint-disable */
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ActionCompleted } from '@/components/animations';
import Popover from '@/components/elements/Popover';
import Button from '@/components/elements/forms/buttons/Button/Button';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import TextArea from '@/components/elements/TextArea';
import { LogoIcon } from '@/components/icons';
import Icon from '@/components/icons/Icon';
import { SCREEN_NAME } from '@/constants/analytics';
import { config } from '@/config';
import { getAuthorInitials } from '@/features/ProfilePages/helpers/review';
import { getHomeDescription, isSistaminuten, trackPage, url } from '@/helpers';
import { getHoursInterval, getSalonDate, makeReadableDayShort } from '@/helpers/date';
import { classnames, isServer } from '@/helpers/general';
import { getImageContain } from '@/helpers/images';
import { getPlaceTimezone } from '@/helpers/placeHelper';
import useScrollToTop from '@/hooks/useScrollToTop';
import { trackScreenShown } from '@/hooks/useTrackScreenShown';
import { _t } from '@/locale';
import { reviewsServices } from '@/services';
import { Switch } from '@/components/elements/forms/Switch';

import { TERMS_URLS } from '@/constants/terms';

type AddReviewProps = {
  match: {
    params: {
      userId: string;
      bookingId: string;
    };
  };
};

type BookingSummary = {
  serviceName?: string;
  start?: number;
  employeeName?: string;
  employeeAvatar?: string;
};

const formatBookingStart = (start: number) => {
  const timezone = getPlaceTimezone(null);
  const weekDay = config.weekDaysShort[getSalonDate(start, timezone).day()];
  return `${weekDay} ${makeReadableDayShort(start * 1000, timezone)} ${getHoursInterval(start * 1000, 0, timezone)}`;
};

const CONSENT_POPOVER_ID = 'add-review-consent-info';

const ConsentInfoPopover = () => {
  const supportsPopover = !isServer && HTMLElement.prototype.hasOwnProperty('popover');
  const [isVisibleFallback, setIsVisibleFallback] = useState(false);

  const toggle = (visible: boolean) => {
    setIsVisibleFallback(visible);
    if (supportsPopover) {
      const popover = document.getElementById(CONSENT_POPOVER_ID) as HTMLElement & {
        showPopover: () => void;
        hidePopover: () => void;
      };
      visible ? popover?.showPopover() : popover?.hidePopover();
    }
  };

  return (
    <>
      <button
        type="button"
        {...{ popovertarget: CONSENT_POPOVER_ID }}
        className={classnames(
          `trigger-${CONSENT_POPOVER_ID}`,
          'focus-visible:outline-black-900 flex rounded-sm focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        )}
        onMouseEnter={() => toggle(true)}
        onMouseLeave={() => toggle(false)}
        onFocus={() => toggle(true)}
        onBlur={() => toggle(false)}
        aria-label={_t('reviews.consentNote')}
        aria-describedby={CONSENT_POPOVER_ID}>
        <Icon variant="info-circle" color="fg_link" style={{ width: 20, height: 20 }} />
      </button>
      {(supportsPopover || isVisibleFallback) && (
        <Popover id={CONSENT_POPOVER_ID} className={classnames('mt-2', { 'z-[100]': !supportsPopover })} align="right">
          <div className="bg-bg_inverse p-md w-full max-w-[400px] rounded-lg">
            <p className="text-fg_secondary_inverse text-m">{_t('reviews.consentNote')}</p>
          </div>
        </Popover>
      )}
    </>
  );
};

function AddReview(props: AddReviewProps) {
  useScrollToTop();

  const parsed = url.getParamsFromGET();
  const { userId, bookingId } = props.match.params;
  const intStars = parseInt(parsed['star']);
  const defaultStarValue = (intStars && intStars > 5 ? 5 : intStars < 0 ? 1 : intStars) || 0;
  const [active, setActive] = useState(true);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [consentToPublishReview, setConsentToPublishReview] = useState(true);
  const [bookingSummary, setBookingSummary] = useState<BookingSummary>({});
  const LIMIT = 1000;
  const [formData, setFormData] = useState<{
    stars: number;
    message: string;
    tempError?: string;
    userId: string;
    edit: boolean;
    bookingId: string;
  }>({
    stars: defaultStarValue,
    message: '',
    tempError: undefined,
    userId,
    edit: false,
    bookingId,
  });

  const canVote = active && dataLoaded && !loading;

  useEffect(() => {
    // Track page
    trackPageVisit();
    //  Get Data
    reviewsServices
      .getReviewData(userId, bookingId, parsed['reviewId'])
      .then((response) => {
        const { expired, review = {}, booking, employee } = response || {};

        const editExistingReview = !!review?.message?.text || !!review?.stars;

        if (expired) {
          setFormData({
            ...formData,
            stars: 0,
          });
          toast(
            ({ closeToast }) => (
              <Snackbar
                type="danger"
                label={_t('reviews.expired')}
                action={<button onClick={closeToast}>{_t('Alright')}</button>}
              />
            ),
            {
              autoClose: false,
            },
          );
          setActive(false);
          return;
        }
        setBookingSummary({
          serviceName: booking?.services?.[0]?.service?.name,
          start: booking?.start,
          employeeName: employee?.about?.name,
          employeeAvatar: employee?.about?.avatar,
        });
        const { text } = review.message || {};
        setFormData({
          ...formData,
          stars: review.stars || defaultStarValue,
          message: text || '',
          bookingId,
          userId,
          edit: editExistingReview,
        });
        setConsentToPublishReview(review.commentConsentedByReviewer ?? true);
        setDataLoaded(true);
      })
      .catch((error) => {
        const errorLabel = error === 'Not found' ? 'reviews.noBooking' : 'serverError';
        setFormData({
          ...formData,
          stars: 0,
        });
        toast(
          ({ closeToast }) => (
            <Snackbar
              type="danger"
              label={_t(errorLabel)}
              action={<button onClick={closeToast}>{_t('Alright')}</button>}
            />
          ),
          {
            autoClose: false,
          },
        );
        setActive(false);
      });
  }, []);

  const seo = () => {
    const title = _t('postReview');
    const description = getHomeDescription();
    const pageUrl = `${url.getBaseUrl()}/addReview`;
    const imageUrl = url.getBaseImageUrl();
    const isNoIndex = isSistaminuten();

    return (
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        {isNoIndex && <meta name="robots" content="noindex" />}
      </Helmet>
    );
  };

  const trackPageVisit = () => {
    trackPage();
    trackScreenShown({ screen_name: SCREEN_NAME.ADD_REVIEW });
  };

  const RATING_LABELS = ['rating-1', 'rating-2', 'rating-3', 'rating-4', 'rating-5'] as const;

  const selectStars = (value: number) => {
    if (!canVote || formData.stars === value) {
      return;
    }
    setFormData({ ...formData, stars: value });
  };

  const saveReview = () => {
    if (!canVote || !formData.stars) {
      return;
    }

    setLoading(true);
    reviewsServices
      .save({ ...formData, commentConsentedByReviewer: consentToPublishReview })
      .then((response) => {
        const { error, saved } = response || {};

        setLoading(false);
        if (!saved) {
          setActive(false);

          const getErrorLabel = (error: string) => {
            switch (error) {
              case 'noShow':
                return 'reviews.noShow';
              case 'noBooking':
                return 'reviews.noBooking';
              case 'expired':
                return 'reviews.expired';
              default:
                return 'serverError';
            }
          };

          toast(
            ({ closeToast }) => (
              <Snackbar
                type="danger"
                label={_t(getErrorLabel(error))}
                action={<button onClick={closeToast}>{_t('Alright')}</button>}
              />
            ),
            {
              autoClose: false,
            },
          );
          return;
        }

        setSaved(true);
      })
      .catch(() => {
        setLoading(false);
        toast(
          ({ closeToast }) => (
            <Snackbar
              type="danger"
              label={_t('serverError')}
              action={<button onClick={closeToast}>{_t('Alright')}</button>}
            />
          ),
          {
            autoClose: false,
          },
        );
      });
  };

  return (
    <>
      <div className="z-9999 pl-md fixed top-0 flex h-[64px] w-full items-center bg-white shadow-sm">
        <Link to="/" aria-label="Bokadirekt">
          <LogoIcon className="h-5 sm:h-6" />
        </Link>
      </div>
      <div className="bg-surface_l4 flex min-h-screen flex-col items-center pt-[64px]">
        <div className="gap-md sm:py-4xl sm:px-4xl flex w-full max-w-[1140px] flex-col items-center">
          <div className="gap-sm pb-md max-sm:px-xl max-sm:pt-xl sm:pt-xl flex w-full max-w-[604px] flex-col items-start">
            {bookingSummary.serviceName && (
              <h1 className="text-h-m text-fg_primary break-words font-bold">{bookingSummary.serviceName}</h1>
            )}
            <div className="gap-md flex w-full items-center">
              {bookingSummary.employeeName && (
                <div className="gap-xs flex min-w-0 items-center">
                  {bookingSummary.employeeAvatar ? (
                    <img
                      src={getImageContain(bookingSummary.employeeAvatar, 'small')}
                      alt=""
                      className="h-xl w-xl shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <span className="bg-bg_brand h-xl w-xl text-fg_primary flex shrink-0 items-center justify-center rounded-full text-xs">
                      {getAuthorInitials(bookingSummary.employeeName)}
                    </span>
                  )}
                  <span className="text-m text-fg_secondary truncate">{bookingSummary.employeeName}</span>
                </div>
              )}
              {!!bookingSummary.start && (
                <span className="text-m text-fg_secondary truncate">{formatBookingStart(bookingSummary.start)}</span>
              )}
            </div>
          </div>
          <div className="gap-xl flex w-full max-w-[604px] flex-col">
            <div className="bg-surface_l2 sm:border-border_regular gap-xl p-lg sm:p-2xl flex w-full flex-col overflow-hidden break-words sm:rounded-lg sm:border">
              {saved ? (
                <div className="flex flex-col items-center">
                  <ActionCompleted style={{ width: 240, height: 240 }} onAnimationComplete={() => {}} />
                  <span>{_t('reviews.commentsaved')}</span>
                </div>
              ) : (
                <>
                  <div className="gap-md flex w-full flex-col items-center sm:flex-row sm:items-start">
                    <div className="gap-2xs flex flex-1 flex-col items-center sm:items-start">
                      <h2 className="text-fg_primary text-xl font-bold">
                        {formData.edit ? _t('pages.addReview.currentReview') : _t('pages.addReview.title')}
                      </h2>
                      {!!formData.stars && (
                        <span className="text-m text-fg_secondary">
                          {_t('pages.addReview.subtitle', {
                            count: formData.stars,
                            label: _t(RATING_LABELS[formData.stars - 1]),
                          })}
                        </span>
                      )}
                    </div>
                    <div className="gap-sm flex flex-1 items-center justify-center">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          type="button"
                          key={star}
                          disabled={!canVote}
                          aria-label={_t(RATING_LABELS[star - 1])}
                          onClick={() => selectStars(star)}
                          className="focus-visible:outline-border_focus rounded-sm focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-default">
                          {star <= formData.stars ? (
                            <Icon variant="star-filled" color="bg_accent_5" style={{ width: 40, height: 40 }} />
                          ) : (
                            <img src="/images/reviews/star-empty.svg" width={40} height={40} alt="" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="gap-xs flex w-full flex-col">
                    {active && dataLoaded && !!formData.stars && (
                      <TextArea
                        rows={5}
                        onChange={(e) => {
                          const { value } = e.target;
                          if (value.length <= LIMIT) {
                            setFormData({ ...formData, message: value });
                          }
                        }}
                        characterLimit={LIMIT}
                        autoFocus={formData.edit}
                        placeholder={_t('pages.addReview.placeholder')}
                        value={formData.message}
                      />
                    )}
                    <div className="gap-md flex w-full items-center justify-between">
                      <div className="gap-xs flex items-center">
                        <span className="text-m text-fg_secondary">{_t('pages.addReview.showReview')}</span>
                        <ConsentInfoPopover />
                      </div>
                      <Switch
                        switched={consentToPublishReview}
                        alt={_t('pages.addReview.showReview')}
                        disabled={!canVote}
                        onChange={() => setConsentToPublishReview(!consentToPublishReview)}
                      />
                    </div>
                  </div>
                  <Button
                    size={'lg'}
                    label={formData.edit ? _t('pages.addReview.update') : _t('pages.addReview.publish')}
                    loading={loading}
                    disabled={!canVote || !formData.stars}
                    onClick={saveReview}
                    block
                  />
                </>
              )}
            </div>
            <p className="text-s text-fg_alwaysDark px-lg sm:px-2xl">
              <span>{_t('reviews.bottomNote')}</span>{' '}
              <a href={TERMS_URLS.PRIVACY_POLICY} target="_blank" rel="noopener noreferrer">
                {_t('readOurPrivacyPolicy')}
              </a>
            </p>
          </div>
        </div>
        {seo()}
      </div>
    </>
  );
}

export default AddReview;
